'use client';
import React, { useState } from 'react';
import { Formik, Form, FormikHelpers } from 'formik';
import { DialogHeader } from '@src/v3/components/coreUI/dialog';
import Icon from '@src/v3/components/coreUI/icon';
import Button from '@src/v3/components/coreUI/button';
import OtpInput from '@src/v3/components/coreUI/otpInput';
import GradientPanelOtp from '@src/v3/assets/svg/gradientPanelOtp';
import PhoneConfirmationCard from '@src/v3/assets/svg/phoneConfirmationCard';
import {
  LoginStepProps,
  LoginFormValues,
} from '@src/v3/interfaces/components/loginModal';
import {
  smsValidationSchema,
  initialValues,
} from '@src/v3/schemas/components/login';
import {
  OTP_VARIANTS,
  LOGIN_STEPS,
  FORM_FIELD_NAMES,
} from '@src/v3/constants/configs';
import { ERROR_MESSAGES } from '@src/v3/constants/messages';
import { formatPhoneNumber } from '@src/v3/utils/helper';

const SmsStep: React.FC<LoginStepProps> = ({
  phone = '',
  onSmsSubmit,
  isSmsLoading = false,
  phoneResendTimer = 0,
  onPhoneResend,
  canResendPhone = false,
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  onStepChange: onStepChange,
  isOAuthFlow = false,
  onClose,
  hideRightPanel = false,
  hideTryAnotherNumber = false,
  hideTryAnotherNumberClick,
}) => {
  const [errorMessage, setErrorMessage] = useState<string>('');

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async (
    values: LoginFormValues,
    { setSubmitting, setFieldValue }: FormikHelpers<LoginFormValues>
  ) => {
    if (!values.code || values.code.trim() === '') {
      setSubmitting(false);
      return;
    }
    if (!/^\d{6}$/.test(values.code)) {
      setSubmitting(false);
      return;
    }

    setErrorMessage(''); // Clear previous error
    try {
      await onSmsSubmit?.({ ...values, code: values.code });
    } catch (error) {
      const apiError = error as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      const errorMsg =
        apiError?.response?.data?.message ||
        apiError?.message ||
        ERROR_MESSAGES.otpCodeInvalid;
      setErrorMessage(errorMsg);
      // Clear OTP input value on error
      setFieldValue(FORM_FIELD_NAMES.CODE, '');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="h-full flex flex-col md:flex-row">
      <div className="md:w-[686px] w-full h-full md:h-[790px] self-stretch px-0 md:px-2.5 pt-0 md:pt-2.5 pb-0 md:pb-8 bg-white rounded-none md:rounded-l-2xl flex flex-col items-start relative">
        {/* Mobile Header */}
        <div className="md:hidden w-full">
          <DialogHeader className="px-5 pt-6 pb-5 flex flex-col gap-3 items-start">
            <div className="self-stretch flex flex-col gap-4 items-start w-full">
              <div className="self-stretch inline-flex justify-start items-center gap-2.5">
                <div className="flex-1 justify-start text-black text-2xl font-medium font-geist leading-9 tracking-[-0.48px]">
                  Check your Phone
                </div>
                <span
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <Icon
                    iconName="crossicon"
                    iconProps={{ className: 'w-5 h-5 text-gray-600' }}
                  />
                </span>
              </div>
              <div className="self-stretch flex flex-col gap-0 items-start">
                <div className="flex-1 justify-start text-gray-800 text-base font-normal font-geist leading-7">
                  We sent a code to your phone:
                </div>
                <div className="flex-1 justify-start text-neutral-900 text-base font-medium font-geist leading-7">
                  {formatPhoneNumber(phone)}
                </div>
                {!hideTryAnotherNumber && (
                  <div className="flex-1 justify-start text-base font-normal font-geist leading-7">
                    <Button
                      type="button"
                      variant="link"
                      className="text-focus underline hover:opacity-75 h-auto p-0 text-base font-normal"
                      onClick={() => {
                        if (hideTryAnotherNumberClick) {
                          hideTryAnotherNumberClick();
                        } else if (isOAuthFlow) {
                          onStepChange?.(LOGIN_STEPS.UPDATE_PHONE);
                        } else {
                          onStepChange?.(LOGIN_STEPS.PHONE);
                        }
                      }}
                    >
                      Try different number?
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </DialogHeader>
        </div>

        {/* Desktop Header */}
        <div className="hidden self-stretch md:flex flex-col justify-start items-start gap-2">
          <div className="self-stretch px-8 pt-16 pb-5 bg-white inline-flex flex-col justify-center items-start gap-3">
            <div className="self-stretch flex flex-col justify-start items-start gap-4">
              <div className="self-stretch inline-flex justify-start items-center gap-2.5">
                <div className="flex-1 justify-start text-gray-1000 text-xl md:text-2xl font-medium font-geist leading-7 md:leading-9">
                  Check your Phone
                </div>
              </div>
              <div className="self-stretch flex flex-col justify-start items-start gap-2.5">
                <div className="flex-1 justify-start">
                  <span className="text-gray-1000 text-base font-normal font-geist leading-6">
                    We sent a code to your phone:{' '}
                  </span>
                  <span className="text-gray-1000 text-base font-normal font-geist leading-6">
                    {formatPhoneNumber(phone)}
                  </span>
                </div>
                {!hideTryAnotherNumber && (
                  <div className="flex-1 justify-start">
                    <Button
                      type="button"
                      variant="link"
                      className="text-focus text-base font-normal font-geist underline leading-6 hover:opacity-75 h-auto p-0"
                      onClick={() => {
                        if (hideTryAnotherNumberClick) {
                          hideTryAnotherNumberClick();
                        } else if (isOAuthFlow) {
                          onStepChange?.(LOGIN_STEPS.UPDATE_PHONE);
                        } else {
                          onStepChange?.(LOGIN_STEPS.PHONE);
                        }
                      }}
                    >
                      Try a different number?
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <Formik
          initialValues={{ ...initialValues, phone }}
          validationSchema={smsValidationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, handleChange, isSubmitting, submitForm }) => (
            <Form className="flex-1 w-full flex flex-col">
              <div className="flex-1 w-full flex flex-col overflow-y-auto md:pb-0">
                {/* Mobile Content */}
                <div className="md:hidden px-5 py-3 flex flex-col gap-8 min-h-full">
                  <OtpInput
                    length={6}
                    value={values.code || ''}
                    onChange={(value) => {
                      handleChange({
                        target: { name: FORM_FIELD_NAMES.CODE, value },
                      });
                      if (errorMessage) {
                        setErrorMessage('');
                      }
                    }}
                    onComplete={submitForm}
                    disabled={isSmsLoading || isSubmitting}
                    error={!!errorMessage}
                    className="flex gap-[10px] items-center justify-start w-full min-w-0"
                    inputClassName="flex-1 min-w-0 h-12 p-2 flex items-center justify-center gap-2.5 rounded-xl border bg-white text-center text-base font-normal font-geist text-gray-800 placeholder:text-gray-800 focus:outline-none tracking-[-0.16px] leading-6"
                    dataAttribute="otp-index"
                    variant={OTP_VARIANTS.MOBILE}
                  />

                  {/* Error Message - Mobile */}
                  {errorMessage && (
                    <div className="inline-flex items-start gap-2">
                      <Icon
                        iconName="warningCircle"
                        iconProps={{
                          className: 'w-5 h-5 flex-shrink-0 mt-0.5',
                        }}
                      />
                      <span className="font-geist font-normal text-[#E5484D] text-[14px] leading-[24px] tracking-[-0.14px]">
                        {errorMessage}
                      </span>
                    </div>
                  )}

                  <div className="flex items-center justify-between w-full">
                    <div className="flex-1 justify-start">
                      <span className="text-gray-1000 text-base font-normal font-geist leading-6 tracking-[-0.16px]">
                        Didn&apos;t get it? Wait a moment, or <br />
                      </span>
                      <Button
                        type="button"
                        variant="link"
                        disabled={
                          isSmsLoading || !canResendPhone || isSubmitting
                        }
                        onClick={() => {
                          setErrorMessage('');
                          onPhoneResend?.();
                        }}
                        className={`text-focus text-base font-normal font-geist underline leading-6 tracking-[-0.16px] hover:opacity-75 h-auto p-0 ${
                          !canResendPhone
                            ? 'text-gray-1000 cursor-not-allowed opacity-50'
                            : ''
                        }`}
                      >
                        Request a New Code.
                      </Button>
                    </div>
                    <div className="justify-start text-gray-700 text-sm font-medium font-geist leading-6 tracking-[-0.14px]">
                      {phoneResendTimer > 0 ? formatTime(phoneResendTimer) : ''}
                    </div>
                  </div>

                  {/* Phone Confirmation Card - Mobile */}
                  <div className="relative overflow-hidden flex-1 -top-8 -mx-5">
                    <PhoneConfirmationCard className="absolute top-0 right-0" />
                  </div>
                </div>

                {/* Desktop Content */}
                <div className="hidden md:flex self-stretch px-8 py-2.5 flex-col justify-start items-start gap-8">
                  <OtpInput
                    length={6}
                    value={values.code || ''}
                    onChange={(value) => {
                      handleChange({
                        target: { name: FORM_FIELD_NAMES.CODE, value },
                      });
                      if (errorMessage) {
                        setErrorMessage('');
                      }
                    }}
                    onComplete={submitForm}
                    disabled={isSmsLoading || isSubmitting}
                    error={!!errorMessage}
                    className="flex items-center justify-start gap-2.5"
                    inputClassName="w-12 h-12 p-2 flex items-center justify-center gap-2.5 rounded-xl border bg-white text-center text-base font-normal font-geist placeholder:text-gray-800 focus:outline-none text-gray-1000 tracking-[-0.16px] leading-6"
                    dataAttribute="otp-index"
                    variant={OTP_VARIANTS.DESKTOP}
                  />

                  {/* Error Message */}
                  {errorMessage && (
                    <div className="self-stretch inline-flex items-start gap-2">
                      <Icon
                        iconName="warningCircle"
                        iconProps={{
                          className: 'w-5 h-5 flex-shrink-0 mt-0.5',
                        }}
                      />
                      <span className="font-geist font-normal text-[#E5484D] text-[14px] leading-[24px] tracking-[-0.14px]">
                        {errorMessage}
                      </span>
                    </div>
                  )}

                  <div className="self-stretch inline-flex justify-between items-center">
                    <div className="w-60 justify-start">
                      <span className="text-gray-1000 text-base font-normal font-geist leading-6">
                        Didn&apos;t get it? Wait a moment, or <br />
                      </span>
                      <Button
                        type="button"
                        variant="link"
                        disabled={
                          isSmsLoading || !canResendPhone || isSubmitting
                        }
                        onClick={() => {
                          setErrorMessage('');
                          onPhoneResend?.();
                        }}
                        className={`text-focus text-base font-normal font-geist underline leading-6 hover:opacity-75 h-auto p-0 ${
                          !canResendPhone
                            ? 'text-gray-1000 cursor-not-allowed opacity-50'
                            : ''
                        }`}
                      >
                        request a new code.
                      </Button>
                    </div>
                    <div className="justify-start text-gray-1000 text-sm font-medium font-geist leading-6">
                      {phoneResendTimer > 0 ? formatTime(phoneResendTimer) : ''}
                    </div>
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
      {!hideRightPanel && (
        <div className="hidden md:flex relative flex-col p-2.5 items-start gap-2.5 self-stretch shrink-0 rounded-r-2xl bg-background-200 overflow-hidden w-[550px] h-[790px]">
          <GradientPanelOtp
            className="absolute inset-0 w-full h-full"
            preserveAspectRatio="none"
          />
        </div>
      )}
    </div>
  );
};

export default SmsStep;
